import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";

export type TeacherMessage = components["schemas"]["TeacherMessageDetails"];
export type TeacherMessageOptions = components["schemas"]["TeacherMessageOptions"];
export type TeacherMessageClassOption = components["schemas"]["TeacherMessageClassOption"];

export const teacherMessageKeys = {
  all: ["teacherMessages"] as const,
  list: (filters: object) => ["teacherMessages", "list", filters] as const,
  options: () => ["teacherMessages", "options"] as const,
};

/**
 * The classes the signed-in teacher teaches today, with their students and reachable guardians (GET /teacher-messages/options).
 * Null when the user is not linked to a teacher profile: a Super admin has teacherMessages.send, but only a teacher sends (403).
 */
export function useTeacherMessageOptions(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: teacherMessageKeys.options(),
    enabled,
    queryFn: async (): Promise<TeacherMessageOptions | null> => {
      const { data, error, response } = await api.GET("/teacher-messages/options");
      if (response.status === 403) return null;
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
}

/** The linked chats a message reaches and the guardians it cannot: of the whole class, or of one of its students. */
export function reachOf(schoolClass: TeacherMessageClassOption, studentId: string | undefined): { chats: number; notLinked: number } {
  const target = (studentId ? schoolClass.students.find((s) => s.studentId === studentId) : undefined) ?? schoolClass;
  return { chats: Number(target.linkedChats), notLinked: Number(target.notLinkedCount) };
}
